import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Heart, Sparkles, BriefcaseMedical, LayoutGrid, LogOut, MapPin, Menu, Loader2, FileText, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { useLocationStore } from "@/store/locationStore";
import { notificationsApi } from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import SmartAppBanner from "@/components/SmartAppBanner";
import PatientSideMenu from "@/components/PatientSideMenu";
import FloatingDrSanjeevani from "@/components/FloatingDrSanjeevani";
import logoWide from "@/assets/trata-health-logo-wide.png";

// Mirrors mobile BottomTabBar: Home · For You · Trata AI · Care Hub · More
const NAV = [
  { to: "/home", label: "Home", icon: Home, color: "#2563EB", id: "home" },
  { to: "/for-you", label: "For You", icon: Heart, color: "#EC4899", id: "for-you" },
  { to: "/records", label: "Records", icon: FileText, color: "#0369A1", id: "records" },
  { to: "/trata-ai", label: "Trata AI", icon: Sparkles, color: "#7C3AED", id: "trata-ai" },
  { to: "/care-hub", label: "Care Hub", icon: BriefcaseMedical, color: "#16A34A", id: "care-hub" },
  { to: "/more", label: "More", icon: LayoutGrid, color: "#F59E0B", id: "more" },
];

function initials(name) {
  if (!name) return "U";
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = useAuthStore((s) => s.displayName());
  const profile = useAuthStore((s) => s.customerProfile);
  const logout = useAuthStore((s) => s.logout);
  const [menuOpen, setMenuOpen] = useState(false);

  const locLabel = useLocationStore((s) => s.label);
  const detecting = useLocationStore((s) => s.detecting);
  const detectLoc = useLocationStore((s) => s.detect);
  const city = locLabel || profile?.basicInfo?.city || profile?.medicalInfo?.city || "India";
  const user = useAuthStore((s) => s.user);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?._id) return;
    const fetchUnread = () =>
      notificationsApi
        .unreadCount(user._id)
        .then((res) => setUnreadCount(res.data?.unreadCount || 0))
        .catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => clearInterval(id);
  }, [user?._id, location.pathname]);

  const handleDetectLocation = async () => {
    const res = await detectLoc();
    if (res.ok) toast.success(`Location set to ${res.label}`);
    else if (res.error === "denied") toast.error("Enable location access in your browser to detect your location.");
    else toast.error("Couldn't fetch your location.");
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <PatientSideMenu open={menuOpen} onOpenChange={setMenuOpen} />
      <FloatingDrSanjeevani />
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card z-30">
        <div className="px-5 py-5 flex items-center gap-2">
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="menu-button-desktop"
            className="h-9 w-9 rounded-xl hover:bg-accent flex items-center justify-center shrink-0"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-foreground" />
          </button>
          <img src={logoWide} alt="Trata Health" className="h-11 lg:h-12 w-auto object-contain object-left" />
          <button
            onClick={() => navigate("/notifications")}
            data-testid="notification-bell-desktop"
            className="relative h-9 w-9 rounded-xl hover:bg-accent flex items-center justify-center shrink-0 ml-auto"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-foreground" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[9px] font-bold flex items-center justify-center border-2 border-card">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </div>
        <nav className="flex-1 px-3 space-y-1 mt-2">
          {NAV.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              data-testid={`nav-${item.id}`}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-colors"
              style={({ isActive }) =>
                isActive
                  ? { backgroundColor: item.color + "1A", color: item.color }
                  : { color: "hsl(var(--muted-foreground))" }
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-5 w-5" style={{ color: isActive ? item.color : "#6B7280" }} />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <NavLink to="/profile" data-testid="nav-profile" className="flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-accent transition-colors">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">{initials(displayName)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{profile?.patientId || "Patient"}</p>
            </div>
          </NavLink>
          <Button variant="ghost" className="w-full justify-start gap-3 mt-1 text-muted-foreground rounded-2xl" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Log out
          </Button>
        </div>
      </aside>

      {/* Main column */}
      <div className="md:pl-64">
        <SmartAppBanner />
        {/* Mobile top header */}
        <header className="md:hidden sticky top-0 z-20 bg-card border-b border-border px-3 h-14 flex items-center gap-2">
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="menu-button"
            className="h-9 w-9 -ml-1 rounded-xl hover:bg-accent flex items-center justify-center shrink-0"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-foreground" />
          </button>
          <img src={logoWide} alt="Trata Health" className="h-8 w-auto max-w-[104px] object-contain object-left shrink-0" />
          <div className="flex-1 min-w-0" />
          <button
            className="flex items-center gap-1 text-sm text-muted-foreground min-w-0 max-w-[42%]"
            onClick={handleDetectLocation}
            disabled={detecting}
            data-testid="header-location-button"
          >
            {detecting ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <MapPin className="h-4 w-4 shrink-0" />}
            <span className="truncate">{detecting ? "Detecting…" : city}</span>
          </button>
          <button
            onClick={() => navigate("/notifications")}
            data-testid="notification-bell"
            className="relative h-9 w-9 rounded-xl hover:bg-accent flex items-center justify-center shrink-0"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-foreground" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[9px] font-bold flex items-center justify-center border-2 border-card">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        </header>

        <main className="px-4 md:px-10 py-6 pb-28 md:pb-10 max-w-5xl mx-auto" key={location.pathname}>
          <div className="animate-fade-in-up">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav — matches mobile app */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border flex items-stretch justify-around pt-2.5 pb-2" style={{ boxShadow: "0 -3px 10px rgba(0,0,0,0.06)" }}>
        {NAV.map((item) => (
          <NavLink key={item.id} to={item.to} data-testid={`bottomnav-${item.id}`} className="relative flex flex-col items-center justify-center gap-1 flex-1 min-h-[52px]">
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute -top-2.5 h-[3px] w-8 rounded-full" style={{ backgroundColor: item.color }} />
                )}
                <item.icon className="h-6 w-6" style={{ color: isActive ? item.color : "#6B7280" }} />
                <span className="text-[11px]" style={{ color: isActive ? item.color : "#6B7280", fontWeight: isActive ? 700 : 500 }}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
